import { useEffect, useState } from "react";

/**
 * CinematicShield — aesthetic anti-capture interference layer.
 *
 * NOT DRM. Cannot prevent screenshots. The trick: per-frame visual
 * interference that the eye smooths via persistence-of-vision but a
 * single-frame capture preserves as artifacts.
 *
 * Layers (stacked, all pointer-events: none, aria-hidden):
 *   1. Holographic shimmer — diagonal cyan/magenta gradient sliding across
 *   2. Scanline interference — repeating-linear-gradient drifting -4px on 1.5s
 *   3. Sub-pixel jitter — applied via .az-advanced-jitter class on I-Z tiles
 *      (animation defined in Tailwind config: micro-jitter)
 *   4. (Optional) Particle dust — only when intensity === "medium" and not mobile
 *
 * Honors prefers-reduced-motion: ALL layers disabled (a11y > aesthetic).
 * Mobile: half intensity, drops particle dust.
 */
export default function CinematicShield({ children, intensity = "medium" }) {
    const [enabled, setEnabled] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const mqMobile = window.matchMedia("(max-width: 767px)");
        const sync = () => {
            setEnabled(!mqMotion.matches);
            setIsMobile(mqMobile.matches);
        };
        sync();
        mqMotion.addEventListener?.("change", sync);
        mqMobile.addEventListener?.("change", sync);
        return () => {
            mqMotion.removeEventListener?.("change", sync);
            mqMobile.removeEventListener?.("change", sync);
        };
    }, []);

    const intensityFactor = intensity === "low" ? 0.6 : intensity === "high" ? 1.4 : 1.0;
    const mobileFactor = isMobile ? 0.5 : 1.0;
    const k = intensityFactor * mobileFactor;

    return (
        <div className="relative" data-cinematic-shield={enabled ? "active" : "off"}>
            {children}

            {enabled && (
                <>
                    {/* Layer 1 — Holographic shimmer */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none overflow-hidden"
                        style={{ mixBlendMode: "screen", zIndex: 25 }}
                    >
                        <div
                            className="absolute -inset-y-4 w-[200%] h-full animate-shimmer-x"
                            style={{
                                background: `linear-gradient(120deg,
                                    transparent 0%,
                                    rgba(0,219,255,${0.06 * k}) 30%,
                                    rgba(255,107,255,${0.04 * k}) 50%,
                                    rgba(0,219,255,${0.06 * k}) 70%,
                                    transparent 100%)`,
                            }}
                        />
                    </div>

                    {/* Layer 2 — Scanline interference */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none overflow-hidden"
                        style={{ mixBlendMode: "overlay", zIndex: 26 }}
                    >
                        <div
                            className="absolute inset-0 animate-scanline-drift"
                            style={{
                                background: `repeating-linear-gradient(
                                    0deg,
                                    rgba(0,219,255,${0.025 * k}) 0px,
                                    rgba(0,219,255,${0.025 * k}) 1px,
                                    transparent 1px,
                                    transparent 4px
                                )`,
                            }}
                        />
                    </div>

                    {/* Layer 4 — Particle dust (desktop, medium+ intensity only) */}
                    {!isMobile && intensity !== "low" && (
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 pointer-events-none overflow-hidden"
                            style={{ zIndex: 24 }}
                        >
                            {Array.from({ length: 22 }).map((_, i) => {
                                const tx = ((i * 137) % 100);
                                const ty = ((i * 71) % 100);
                                const dur = 16 + (i % 7) * 2;
                                const delay = (i % 5) * -3;
                                return (
                                    <span
                                        key={i}
                                        className="absolute rounded-full"
                                        style={{
                                            left: `${tx}%`,
                                            top: `${ty}%`,
                                            width: `${1 + (i % 2)}px`,
                                            height: `${1 + (i % 2)}px`,
                                            background: "rgba(0,219,255,0.18)",
                                            boxShadow: "0 0 4px rgba(0,219,255,0.35)",
                                            animation: `cs-dust-drift ${dur}s ease-in-out ${delay}s infinite`,
                                        }}
                                    />
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Local keyframes + the I-Z micro-jitter rule */}
            <style>{`
                @keyframes cs-dust-drift {
                    0%   { transform: translate(0, 0); opacity: 0.6; }
                    50%  { transform: translate(8px, -12px); opacity: 1; }
                    100% { transform: translate(0, 0); opacity: 0.6; }
                }
                /* Sub-pixel jitter class applied to advanced (I-Z) tiles only */
                .az-advanced-jitter {
                    animation: az-jitter 0.08s steps(4) infinite;
                    will-change: transform;
                }
                @keyframes az-jitter {
                    0%, 100% { transform: translate(0, 0); }
                    25%      { transform: translate(0.3px, 0); }
                    50%      { transform: translate(0, 0.3px); }
                    75%      { transform: translate(-0.3px, -0.2px); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .az-advanced-jitter { animation: none; }
                }
            `}</style>
        </div>
    );
}
